import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ArrowUpRight } from 'lucide-react';
import DOMPurify from 'dompurify';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { coverSlotForPost, SLOT_DEFAULTS, avatarForAuthor } from '../lib/imagery';
import { useBrandImagesMap } from '../lib/BrandImagesContext';
import { useJsonLd, breadcrumbSchema, siteOrigin } from '../lib/seo';

function resolvePostCover(post, overrides) {
    if (post?.cover_image) return post.cover_image;
    const slot = coverSlotForPost(post);
    return (overrides && overrides[slot]) || SLOT_DEFAULTS[slot];
}

function renderMarkdown(md) {
    if (!md) return '';
    const lines = md.split('\n');
    const out = [];
    let inUl = false;
    const flush = () => { if (inUl) { out.push('</ul>'); inUl = false; } };
    const inline = (s) =>
        s
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/`([^`]+)`/g, '<code>$1</code>')
            .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            .replace(/\*([^*]+)\*/g, '<em>$1</em>');
    for (const raw of lines) {
        const line = raw;
        if (/^##\s+/.test(line)) { flush(); out.push(`<h2>${inline(line.replace(/^##\s+/, ''))}</h2>`); continue; }
        if (/^###\s+/.test(line)) { flush(); out.push(`<h3>${inline(line.replace(/^###\s+/, ''))}</h3>`); continue; }
        if (/^\-\s+/.test(line)) {
            if (!inUl) { out.push('<ul>'); inUl = true; }
            out.push(`<li>${inline(line.replace(/^\-\s+/, ''))}</li>`);
            continue;
        }
        if (line.trim() === '') { flush(); out.push(''); continue; }
        flush();
        out.push(`<p>${inline(line)}</p>`);
    }
    flush();
    return out.join('\n');
}

export default function BlogDetailPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);
    const { overrides } = useBrandImagesMap();

    useEffect(() => {
        api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch((e) => setError(e?.response?.data?.detail || 'Not found'));
    }, [slug]);

    // Render Markdown -> HTML, then sanitize with DOMPurify (strict allow-list) to prevent XSS
    const safeHtml = useMemo(() => {
        if (!post?.content) return '';
        const raw = renderMarkdown(post.content);
        return DOMPurify.sanitize(raw, {
            ALLOWED_TAGS: ['h2', 'h3', 'p', 'ul', 'ol', 'li', 'strong', 'em', 'code', 'br', 'blockquote'],
            ALLOWED_ATTR: [],
            KEEP_CONTENT: true,
        });
    }, [post?.content]);

    // Article schema + dynamic <title> / og:image / description for each post
    const articleSchema = useMemo(() => {
        if (!post) return null;
        const origin = siteOrigin();
        const url = `${origin}/blog/${post.slug}`;
        const image = resolvePostCover(post, overrides);
        return {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            '@id': `${url}#article`,
            mainEntityOfPage: { '@type': 'WebPage', '@id': url },
            headline: post.title,
            description: post.excerpt,
            image: image ? [image] : undefined,
            datePublished: post.published_at,
            dateModified: post.updated_at || post.published_at,
            inLanguage: 'en',
            keywords: (post.tags || []).join(', '),
            wordCount: (post.content || '').split(/\s+/).filter(Boolean).length,
            timeRequired: `PT${post.reading_time_min || 5}M`,
            author: {
                '@type': 'Person',
                name: post.author || 'DigiTech Cloud AI Team',
            },
            publisher: {
                '@type': 'Organization',
                '@id': `${origin}/#organization`,
                name: 'DigiTech Cloud AI',
                logo: {
                    '@type': 'ImageObject',
                    url: `${origin}/favicon.png`,
                },
            },
            articleSection: (post.tags || [])[0] || 'Technology',
        };
    }, [post, overrides]);

    // Keep <title>, meta description and og:image in sync with the article
    useEffect(() => {
        if (!post) return undefined;
        const origin = siteOrigin();
        const image = resolvePostCover(post, overrides);
        const url = `${origin}/blog/${post.slug}`;
        const prevTitle = document.title;
        document.title = `${post.title} · DigiTech Cloud AI`;
        const upsert = (selector, attr, value) => {
            let m = document.querySelector(selector);
            if (!m) {
                m = document.createElement('meta');
                const [_, a, name] = selector.match(/\[(name|property)="([^"]+)"\]/) || [];
                if (a && name) m.setAttribute(a, name);
                document.head.appendChild(m);
            }
            m.setAttribute(attr, value);
            return m;
        };
        upsert('meta[name="description"]', 'content', post.excerpt || '');
        upsert('meta[property="og:title"]', 'content', post.title);
        upsert('meta[property="og:description"]', 'content', post.excerpt || '');
        upsert('meta[property="og:url"]', 'content', url);
        upsert('meta[property="og:type"]', 'content', 'article');
        if (image) upsert('meta[property="og:image"]', 'content', image);
        upsert('meta[name="twitter:title"]', 'content', post.title);
        upsert('meta[name="twitter:description"]', 'content', post.excerpt || '');
        if (image) upsert('meta[name="twitter:image"]', 'content', image);
        // Canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', url);
        return () => { document.title = prevTitle; };
    }, [post, overrides]);

    const breadcrumbs = useMemo(() => {
        if (!post) return null;
        const origin = siteOrigin();
        return breadcrumbSchema([
            { name: 'Home',  url: `${origin}/` },
            { name: 'Blog',  url: `${origin}/blog` },
            { name: post.title, url: `${origin}/blog/${post.slug}` },
        ]);
    }, [post]);

    useJsonLd('article', articleSchema);
    useJsonLd('breadcrumb', breadcrumbs);

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-4 pt-40 pb-24 text-center">
                <h1 className="font-display text-3xl font-semibold">Article not found</h1>
                <p className="mt-2 text-muted-foreground">{error}</p>
                <Button asChild className="mt-6"><Link to="/blog">Back to blog</Link></Button>
            </div>
        );
    }
    if (!post) return <div className="pt-40 text-center text-muted-foreground">Loading...</div>;

    return (
        <article data-testid="blog-detail-page" className="relative">
            <section className="relative pt-28 lg:pt-32 pb-8">
                <div className="absolute inset-0 -z-10 dt-grid-overlay dt-noise pointer-events-none" />
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-primary">
                        <ArrowLeft className="h-4 w-4" /> Back to blog
                    </Link>
                    <div className="mt-6 flex flex-wrap items-center gap-2">
                        {(post.tags || []).map((t) => (
                            <span key={t} className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary bg-primary/10 border border-primary/30 px-2.5 py-0.5 rounded-full">
                                {t}
                            </span>
                        ))}
                    </div>
                    <h1 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
                        {post.title}
                    </h1>
                    {/* Author + meta row */}
                    <div className="mt-5 flex items-center flex-wrap gap-3 sm:gap-4 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                        {post.author ? (
                            <div className="flex items-center gap-2 pr-3 border-r border-white/10">
                                <img
                                    src={avatarForAuthor(post.author)}
                                    alt=""
                                    aria-hidden
                                    loading="lazy"
                                    className="h-8 w-8 rounded-full object-cover border border-white/15"
                                    style={{ filter: 'saturate(0.8)' }}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                                <span className="text-foreground/85 normal-case tracking-normal text-sm font-sans">{post.author}</span>
                            </div>
                        ) : null}
                        <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {new Date(post.published_at).toLocaleDateString()}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.reading_time_min} min read</span>
                    </div>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="rounded-2xl overflow-hidden border border-white/10 dt-glow-cyan">
                    <img src={resolvePostCover(post, overrides)} alt={post.title} className="w-full h-auto object-cover" />
                </div>
            </div>

            <section className="py-12 lg:py-16">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="dt-prose" dangerouslySetInnerHTML={{ __html: safeHtml }} />
                </div>
            </section>

            <section className="pb-24">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="dt-glass rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <div className="font-display text-xl font-semibold">Want this kind of work for your team?</div>
                            <div className="text-sm text-muted-foreground mt-1">Speak with a senior technology specialist about your objectives and next steps.</div>
                        </div>
                        <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                            <Link to="/contact" className="inline-flex items-center gap-1.5">Book a Consultation <ArrowUpRight className="h-4 w-4" /></Link>
                        </Button>
                    </div>
                </div>
            </section>
        </article>
    );
}
